import React from "react";
import PropTypes from "prop-types";
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { NumberFormatCustom } from "../formHelpers";
import { useBondRetrievalBasicInfoState } from "../hooks/useBondRetrievalBasicInfoState";
import { RETRIEVE_GLOBAL_DATA_ROLE_DISABLED } from "../../../../pages/SHARED/Bond Retrieval/components/bondRetrieveSharedPropTypes";

function renderBondSubCategory(config, data, isDisabled, handleAutocompleteChange) {
  if (!config.bondSubCategoryOptions) return null;
  return (
    <Autocomplete
      options={config.bondSubCategoryOptions}
      value={data.bondSubCategory || ""}
      disabled={isDisabled}
      onChange={(event, value) => handleAutocompleteChange(event, value, "bondSubCategory")}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Bond Sub Category"
          fullWidth
          variant="outlined"
          required={config.bondSubCategoryRequired !== false}
          disabled={isDisabled}
        />
      )}
    />
  );
}

function renderCounty(config, data, isDisabled, counties, loadingCounties, handleAutocompleteChange) {
  if (!config.showCounty) return null;
  return (
    <Autocomplete
      options={counties}
      value={data.county || ""}
      disabled={isDisabled}
      onChange={(event, value) => handleAutocompleteChange(event, value, "county")}
      loading={loadingCounties}
      renderInput={(params) => (
        <TextField
          {...params}
          label="County"
          fullWidth
          variant="outlined"
          required
          disabled={isDisabled}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCounties ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

function renderProbateCountyFields(config, data, isDisabled, handleChange) {
  if (!config.probateCountyConditionals) return null;
  return (
    <>
      {data.county === "Pasco" && (
        <TextField
          disabled={isDisabled}
          select
          label={config.judgeSternLabel}
          name="judgeStern"
          value={data.judgeStern || "Yes"}
          onChange={handleChange}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      )}
      {data.county === "Pinellas" && (
        <TextField
          disabled={isDisabled}
          select
          label="Is this bond is for the purposes of opening a Wrongful Death Lawsuit?"
          name="wrongfulDeathLawsuit"
          value={data.wrongfulDeathLawsuit || "No"}
          onChange={handleChange}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      )}
    </>
  );
}

function renderNameFields(config, data, isDisabled, handleChange, handleAutocompleteChange) {
  const { nameFields } = config;
  if (nameFields.mode === "trustee") {
    return (
      <>
        <TextField
          disabled={isDisabled}
          label={nameFields.legalNameLabel}
          name="legalName"
          value={data.legalName || ""}
          onChange={handleChange}
          fullWidth
          required
        />
        <TextField
          disabled={isDisabled}
          label={nameFields.trustNameLabel}
          name="trustName"
          value={data.trustName || ""}
          onChange={handleChange}
          fullWidth
          required
        />
      </>
    );
  }

  const maxLength = nameFields.maxLength || 200;
  const aliasesMaxLength = nameFields.aliasesMaxLength;
  return (
    <>
      <TextField
        disabled={isDisabled}
        label={nameFields.firstLabel}
        name="firstName"
        value={data.firstName || ""}
        onChange={handleChange}
        fullWidth
        required
        slotProps={{ htmlInput: { maxLength } }}
      />
      <TextField
        disabled={isDisabled}
        label={nameFields.middleLabel}
        name="middleName"
        value={data.middleName || ""}
        onChange={handleChange}
        fullWidth
        slotProps={{ htmlInput: { maxLength } }}
      />
      <TextField
        disabled={isDisabled}
        label={nameFields.lastLabel}
        name="lastName"
        value={data.lastName || ""}
        onChange={handleChange}
        fullWidth
        required
        slotProps={{ htmlInput: { maxLength } }}
      />
      {nameFields.showSuffix ? (
        <Autocomplete
          options={["Jr", "Sr", "I", "II", "III", "IV"]}
          value={data.suffix || ""}
          disabled={isDisabled}
          onChange={(event, value) => handleAutocompleteChange(event, value, "suffix")}
          renderInput={(params) => (
            <TextField
              disabled={isDisabled}
              {...params}
              label="Suffix"
              fullWidth
              variant="outlined"
            />
          )}
        />
      ) : null}
      {nameFields.showAliases ? (
        <TextField
          disabled={isDisabled}
          label={nameFields.aliasesLabel || "Aliases"}
          name="aliases"
          value={data.aliases || ""}
          onChange={handleChange}
          fullWidth
          slotProps={{
            ...(aliasesMaxLength ? { htmlInput: { maxLength: aliasesMaxLength } } : {}),
          }}
        />
      ) : null}
    </>
  );
}

function renderDateField(config, data, isDisabled, handleChange) {
  const { dateField } = config;
  return (
    <TextField
      disabled={isDisabled}
      label={dateField.label}
      name={dateField.name}
      value={data[dateField.name] || ""}
      onChange={handleChange}
      type="date"
      fullWidth
      required
      slotProps={{ inputLabel: { shrink: true } }}
    />
  );
}

function renderBondAmountField(data, isDisabled, handleChange) {
  return (
    <TextField
      disabled={isDisabled}
      label="Bond Amount"
      name="bondAmount"
      value={data.bondAmount || ""}
      onChange={handleChange}
      fullWidth
      required
      slotProps={{ input: { inputComponent: NumberFormatCustom } }}
    />
  );
}

function renderPremiumField(data, formatAsCurrency) {
  return (
    <TextField
      label="Premium Amount"
      name="premiumAmount"
      value={formatAsCurrency(data.premiumAmount)}
      fullWidth
      required
      disabled
    />
  );
}

function renderCourtCaseField(config, data, isDisabled, handleChange) {
  const { courtCaseNumberLabel, courtCaseRequired } = config;
  return (
    <TextField
      disabled={isDisabled}
      label={courtCaseNumberLabel}
      name="courtCaseNumber"
      value={data.courtCaseNumber || ""}
      onChange={handleChange}
      fullWidth
      required={!!courtCaseRequired}
    />
  );
}

function renderPrincipalField(config, data, isDisabled, handleChange) {
  const { principalField, principalInputType = "number" } = config;
  return (
    <TextField
      disabled={isDisabled}
      label={principalField.label}
      name={principalField.name}
      value={data[principalField.name] || (principalInputType === "number" ? 1 : "")}
      onChange={handleChange}
      fullWidth
      required
      type={principalInputType === "number" ? "number" : undefined}
      slotProps={
        principalInputType === "number"
          ? { input: { inputProps: { min: 1, max: 4 } } }
          : undefined
      }
    />
  );
}

function renderEsignatureField(config, data, isDisabled, handleChange) {
  if (config.esignatureMode === "none") return null;
  return (
    <TextField
      select
      label="Will you prefer to e-sign the Indemnity Agreement?"
      name="is_esignatures"
      value={data.is_esignatures ?? ""}
      onChange={handleChange}
      disabled={isDisabled}
      fullWidth
      required
    >
      <MenuItem value="Yes">Yes</MenuItem>
      <MenuItem value="No">No</MenuItem>
    </TextField>
  );
}

function renderDefaultFieldOrder(ctx) {
  const {
    config,
    data,
    isDisabled,
    counties,
    loadingCounties,
    formatAsCurrency,
    handleChange,
    handleAutocompleteChange,
  } = ctx;

  return (
    <>
      {renderBondSubCategory(config, data, isDisabled, handleAutocompleteChange)}
      {renderCounty(config, data, isDisabled, counties, loadingCounties, handleAutocompleteChange)}
      {renderProbateCountyFields(config, data, isDisabled, handleChange)}
      {renderNameFields(config, data, isDisabled, handleChange, handleAutocompleteChange)}
      {renderDateField(config, data, isDisabled, handleChange)}
      {renderBondAmountField(data, isDisabled, handleChange)}
      {renderPremiumField(data, formatAsCurrency)}
      {renderCourtCaseField(config, data, isDisabled, handleChange)}
      {renderPrincipalField(config, data, isDisabled, handleChange)}
      {renderEsignatureField(config, data, isDisabled, handleChange)}
    </>
  );
}

function renderGuardianshipFieldOrder(ctx) {
  const {
    config,
    data,
    isDisabled,
    counties,
    loadingCounties,
    formatAsCurrency,
    handleChange,
    handleAutocompleteChange,
  } = ctx;

  return (
    <>
      {renderBondSubCategory(config, data, isDisabled, handleAutocompleteChange)}
      {renderCounty(config, data, isDisabled, counties, loadingCounties, handleAutocompleteChange)}
      {renderBondAmountField(data, isDisabled, handleChange)}
      {renderPremiumField(data, formatAsCurrency)}
      {renderNameFields(config, data, isDisabled, handleChange, handleAutocompleteChange)}
      {renderDateField(config, data, isDisabled, handleChange)}
      {renderCourtCaseField(config, data, isDisabled, handleChange)}
      {renderPrincipalField(config, data, isDisabled, handleChange)}
      {renderEsignatureField(config, data, isDisabled, handleChange)}
    </>
  );
}

const BondRetrievalBasicInformationBase = ({
  config,
  globalData,
  data = {},
  updateData,
  role,
  isDisabled = false,
}) => {
  const {
    counties,
    loadingCounties,
    formatAsCurrency,
    handleChange,
    handleAutocompleteChange,
  } = useBondRetrievalBasicInfoState({ data, updateData, config });

  const fieldCtx = {
    config,
    data,
    isDisabled,
    counties,
    loadingCounties,
    formatAsCurrency,
    handleChange,
    handleAutocompleteChange,
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Basic Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        {config.fieldOrder === "guardianship"
          ? renderGuardianshipFieldOrder(fieldCtx)
          : renderDefaultFieldOrder(fieldCtx)}
      </Box>
    </Box>
  );
};

BondRetrievalBasicInformationBase.propTypes = {
  config: PropTypes.object.isRequired,
  ...RETRIEVE_GLOBAL_DATA_ROLE_DISABLED,
};

export default BondRetrievalBasicInformationBase;
